import { Head, Link, router, usePage } from "@inertiajs/react"
import { GearIcon, PaperPlaneIcon, PlusIcon } from "@radix-ui/react-icons"
import { useMemo, useState } from "react"

import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"

function formatTime(value) {
  if (!value) return "-"

  return new Date(value).toLocaleString()
}

function CitationList({ citations = [] }) {
  if (citations.length === 0) return null

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {citations.map((citation, index) => (
        <a
          className="rounded-full bg-white px-3 py-1 text-xs font-medium text-[var(--color-verdigris)] ring-1 ring-[rgb(42_157_143/0.22)] hover:bg-[var(--color-panel)]"
          href={citation.url || "#"}
          key={`${citation.document_id || citation.title}-${citation.page || index}`}
          rel="noreferrer"
          target={citation.url ? "_blank" : undefined}
        >
          {citation.title || "Source"}{citation.page ? `, p. ${citation.page}` : ""}
        </a>
      ))}
    </div>
  )
}

function MessageBubble({ message }) {
  const isUser = message.role === "user"

  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
      <article
        className={cn(
          "max-w-[min(46rem,92%)] rounded-2xl px-4 py-3 shadow-[var(--shadow-soft)]",
          isUser
            ? "bg-[var(--color-charcoal-blue)] text-white"
            : "border border-[var(--color-border)] bg-white text-[var(--color-text)]"
        )}
      >
        <p className="whitespace-pre-wrap text-sm leading-6">{message.content}</p>
        {!isUser ? <CitationList citations={message.citations} /> : null}
      </article>
    </div>
  )
}

function documentTypeLabel(type) {
  return type === "url" ? "URL" : "PDF"
}

function DocumentsTable({ documents }) {
  return (
    <div className="overflow-x-auto rounded-[1.75rem] bg-white/82 p-5 shadow-[var(--shadow-card)] ring-1 ring-[var(--color-border)]">
      <table className="min-w-full text-left text-sm">
        <thead className="text-[var(--color-text-faint)]">
          <tr>
            <th className="px-2 py-2 font-semibold">Document</th>
            <th className="px-2 py-2 font-semibold">Type</th>
            <th className="px-2 py-2 font-semibold">Status</th>
            <th className="px-2 py-2 font-semibold">Pages</th>
            <th className="px-2 py-2 font-semibold">Chunks</th>
            <th className="px-2 py-2 font-semibold">Indexed</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((document) => (
            <tr className="border-t border-[var(--color-border)]" key={document.id}>
              <td className="px-2 py-3">
                {document.source_url ? (
                  <a
                    className="font-medium text-[var(--color-charcoal-blue)] underline decoration-[rgb(42_157_143/0.35)] underline-offset-4 hover:text-[var(--color-verdigris)]"
                    href={document.source_url}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {document.title}
                  </a>
                ) : document.file_url ? (
                  <a
                    className="font-medium text-[var(--color-charcoal-blue)] underline decoration-[rgb(42_157_143/0.35)] underline-offset-4 hover:text-[var(--color-verdigris)]"
                    href={document.file_url}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {document.title}
                  </a>
                ) : (
                  <span className="font-medium text-[var(--color-charcoal-blue)]">{document.title}</span>
                )}
              </td>
              <td className="px-2 py-3">
                <span className="rounded-full bg-[var(--color-panel)] px-3 py-1 text-xs font-semibold uppercase text-[var(--color-text-muted)]">
                  {documentTypeLabel(document.document_type)}
                </span>
              </td>
              <td className="px-2 py-3">{document.status}</td>
              <td className="px-2 py-3">{document.pages_count}</td>
              <td className="px-2 py-3">{document.chunks_count}</td>
              <td className="px-2 py-3">{formatTime(document.indexed_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {documents.length === 0 ? (
        <p className="rounded-lg border border-dashed border-[var(--color-border)] p-5 text-sm text-[var(--color-text-muted)]">
          No indexed documents are available yet.
        </p>
      ) : null}
    </div>
  )
}

export default function OrganizationShowPage({
  chat_enabled = false,
  chat_endpoint,
  conversations = [],
  documents = [],
  selected_conversation = null
}) {
  const { organization, user } = usePage().props
  const [conversation, setConversation] = useState(selected_conversation)
  const [conversationList, setConversationList] = useState(conversations)
  const [question, setQuestion] = useState("")
  const [error, setError] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState(chat_enabled ? "chat" : "documents")
  const messages = conversation?.messages || []
  const activeConversationId = conversation?.id || selected_conversation?.id
  const orgPath = `/orgs/${organization.slug}`

  const orderedConversations = useMemo(() => {
    const byId = new Map(conversationList.map((item) => [item.id, item]))
    if (conversation?.id) byId.set(conversation.id, conversation)

    return Array.from(byId.values()).sort((a, b) => {
      const aTime = new Date(a.last_message_at || a.created_at || 0).getTime()
      const bTime = new Date(b.last_message_at || b.created_at || 0).getTime()
      return bTime - aTime
    })
  }, [conversation, conversationList])

  const selectConversation = (nextConversation) => {
    setConversation(nextConversation)
    router.visit(`${orgPath}?conversation_id=${nextConversation.id}`, {
      only: ["selected_conversation"],
      preserveState: true,
      preserveScroll: true,
      replace: true,
      onSuccess: (page) => setConversation(page.props.selected_conversation)
    })
  }

  const submitQuestion = async (event) => {
    event.preventDefault()

    const trimmedQuestion = question.trim()
    if (!trimmedQuestion || submitting || !chat_enabled) return

    setError("")
    setSubmitting(true)

    const optimisticUserMessage = {
      id: `pending-user-${Date.now()}`,
      role: "user",
      content: trimmedQuestion,
      citations: [],
      metadata: {},
      created_at: new Date().toISOString()
    }

    setQuestion("")
    setConversation((current) => current
      ? { ...current, messages: [ ...(current.messages || []), optimisticUserMessage ] }
      : {
          id: null,
          title: trimmedQuestion,
          methodology_label: null,
          messages: [ optimisticUserMessage ],
          created_at: new Date().toISOString(),
          last_message_at: new Date().toISOString()
        })

    try {
      const response = await fetch(chat_endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          question: trimmedQuestion,
          conversation_id: activeConversationId
        })
      })
      const payload = await response.json()

      if (!response.ok) throw new Error(payload.error || "Unable to send message.")

      if (payload.conversation) {
        setConversation(payload.conversation)
        setConversationList((current) => {
          const withoutCurrent = current.filter((item) => item.id !== payload.conversation.id)
          return [ payload.conversation, ...withoutCurrent ]
        })
        window.history.replaceState({}, "", `${orgPath}?conversation_id=${payload.conversation.id}`)
      } else {
        setConversation((current) => ({
          ...(current || { title: trimmedQuestion, messages: [] }),
          methodology_label: payload.methodology?.label,
          messages: [
            ...((current?.messages) || [ optimisticUserMessage ]),
            {
              id: `guest-assistant-${Date.now()}`,
              role: "assistant",
              content: payload.answer,
              citations: payload.citations || [],
              metadata: payload.metadata || {},
              created_at: new Date().toISOString()
            }
          ]
        }))
      }
    } catch (caughtError) {
      setError(caughtError.message)
      setConversation(selected_conversation)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Head title={organization.name} />

      <main className="min-h-screen bg-[var(--color-app-bg)] text-[var(--color-text)]">
        <div className="w-full px-2 py-3 sm:px-3 lg:px-4 lg:py-4 xl:px-5">
          <header className="rounded-[1.8rem] border border-[var(--color-border)] bg-white/78 px-4 py-4 shadow-[var(--shadow-card)] backdrop-blur-xl sm:px-5 lg:px-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-[var(--color-text-faint)]">
                  Knowledge Viewer
                </p>
                <h1 className="mt-2 text-3xl font-semibold text-[var(--color-charcoal-blue)]">{organization.name}</h1>
              </div>

              <div className="flex flex-wrap gap-3">
                {organization.admin ? (
                  <Button asChild type="button" variant="outline">
                    <Link href={`${orgPath}/settings/assistant`}>
                      <GearIcon className="mr-2 h-4 w-4" />
                      Settings
                    </Link>
                  </Button>
                ) : null}
                {user ? (
                  <Button asChild type="button" variant="outline">
                    <Link href="/settings/user">Profile</Link>
                  </Button>
                ) : (
                  <Button asChild type="button" variant="outline">
                    <a href="/login">Sign in</a>
                  </Button>
                )}
              </div>
            </div>
          </header>
        </div>

        <div className="w-full px-2 pb-4 pt-5 sm:px-3 lg:px-4 lg:pt-6 xl:px-5">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-5">
              {chat_enabled ? <TabsTrigger value="chat">Chat</TabsTrigger> : null}
              <TabsTrigger value="documents">Documents</TabsTrigger>
            </TabsList>

            {chat_enabled ? (
              <TabsContent value="chat">
                <div className="grid min-h-[calc(100vh-16rem)] gap-5 xl:grid-cols-[20rem_minmax(0,1fr)]">
                  <aside className="rounded-[1.75rem] bg-white/82 p-4 shadow-[var(--shadow-card)] ring-1 ring-[var(--color-border)]">
                    <div className="flex items-center justify-between gap-3">
                      <h2 className="text-base font-semibold text-[var(--color-charcoal-blue)]">History</h2>
                      <span className="rounded-full bg-[var(--color-panel)] px-3 py-1 text-xs font-medium text-[var(--color-text-muted)]">
                        {orderedConversations.length}
                      </span>
                    </div>

                    <div className="mt-4 space-y-2">
                      <Button asChild className="w-full" type="button" variant="outline">
                        <Link href={orgPath}>
                          <PlusIcon className="mr-2 h-4 w-4" />
                          New conversation
                        </Link>
                      </Button>

                      {orderedConversations.length > 0 ? orderedConversations.map((item) => {
                        const active = item.id === activeConversationId

                        return (
                          <button
                            className={cn(
                              "block w-full rounded-lg border p-3 text-left transition-colors",
                              active
                                ? "border-[var(--color-charcoal-blue)] bg-[var(--color-charcoal-blue)] text-white"
                                : "border-[var(--color-border)] bg-white text-[var(--color-text-muted)] hover:bg-[var(--color-panel)]"
                            )}
                            key={item.id}
                            onClick={() => selectConversation(item)}
                            type="button"
                          >
                            <p className={cn("truncate text-sm font-semibold", active ? "text-white" : "text-[var(--color-charcoal-blue)]")}>
                              {item.title}
                            </p>
                            <p className={cn("mt-2 truncate text-xs", active ? "text-white/72" : "text-[var(--color-text-faint)]")}>
                              {item.methodology_label || "Knowledgebase chat"} - {formatTime(item.last_message_at || item.created_at)}
                            </p>
                          </button>
                        )
                      }) : (
                        <p className="rounded-lg border border-dashed border-[var(--color-border)] p-4 text-sm text-[var(--color-text-muted)]">
                          {user ? "No conversations yet." : "Guest conversations are not saved."}
                        </p>
                      )}
                    </div>
                  </aside>

                  <section className="flex min-h-0 flex-col rounded-[1.75rem] bg-white/82 shadow-[var(--shadow-card)] ring-1 ring-[var(--color-border)]">
                    <div className="border-b border-[var(--color-border)] px-5 py-4">
                      <h2 className="text-lg font-semibold text-[var(--color-charcoal-blue)]">
                        {conversation?.title || "New conversation"}
                      </h2>
                      {conversation?.methodology_label ? (
                        <p className="mt-1 text-xs font-medium text-[var(--color-text-faint)]">{conversation.methodology_label}</p>
                      ) : null}
                    </div>

                    <div className="flex-1 space-y-4 overflow-y-auto p-5">
                      {messages.length > 0 ? messages.map((message) => (
                        <MessageBubble key={message.id} message={message} />
                      )) : (
                        <div className="rounded-lg border border-dashed border-[var(--color-border)] p-5 text-sm leading-6 text-[var(--color-text-muted)]">
                          Ask a question against this organization&apos;s indexed documents.
                        </div>
                      )}
                    </div>

                    <form className="border-t border-[var(--color-border)] p-4" onSubmit={submitQuestion}>
                      {error ? <p className="mb-3 text-sm text-[var(--color-danger)]">{error}</p> : null}
                      <div className="flex flex-col gap-3 md:flex-row">
                        <textarea
                          className="input-field min-h-24 flex-1 resize-y"
                          onChange={(event) => setQuestion(event.target.value)}
                          placeholder="Ask a question"
                          value={question}
                        />
                        <Button className="md:self-end" disabled={submitting || question.trim().length === 0} type="submit">
                          <PaperPlaneIcon className="mr-2 h-4 w-4" />
                          Send
                        </Button>
                      </div>
                    </form>
                  </section>
                </div>
              </TabsContent>
            ) : null}

            <TabsContent value="documents">
              <DocumentsTable documents={documents} />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </>
  )
}
